import React, { useState, useEffect } from 'react';
import { useOrder } from '../../context/OrderContext';
import { normalizeProfileType } from '../../utils/formatters';
import { api } from '../../services/api';

// Profiles that only support 3m bars (no 6m option)
const PROFILES_ONLY_3M = ['176', '186', '6S'];

const BarConfigSelector = ({ onSelect }) => {
    const { state } = useOrder();
    const rawProfileType = state.orderDetails?.profileType || '';
    const isOnly3m = PROFILES_ONLY_3M.includes(rawProfileType) || PROFILES_ONLY_3M.includes(normalizeProfileType(rawProfileType));
    const existingGroups = state.orderDetails?.processing?.barGroups;
    const [barGroups, setBarGroups] = useState(() => {
        if (existingGroups && existingGroups.length > 0) return [...existingGroups];
        const cb = state.orderDetails?.processing?.currentBar;
        if (cb?.barLength && cb?.quantity) return [{ barLength: cb.barLength, quantity: cb.quantity }];
        return [];
    });
    const [selectedLength, setSelectedLength] = useState(null);
    const [quantity, setQuantity] = useState('');
    const [articleData, setArticleData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [validationError, setValidationError] = useState(null);

    const getCurrentProductCode = () => {
        return state.orderDetails?.productCode || '';
    };

    const fetchArticleData = async (productCode) => {
        if (!productCode) return null;
        try {
            setLoading(true);
            setError(null);
            const response = await api.getArticleById(productCode);
            return response.article || response;
        } catch (err) {
            console.error('Error fetching article:', err);
            setError('Errore nel recupero dati articolo');
            return null;
        } finally {
            setLoading(false);
        }
    };

    const validateQuantity = (qty, minimumSaleQty, barLengthInMeters) => {
        const quantityNum = parseInt(qty, 10);
        if (isNaN(quantityNum) || quantityNum <= 0) {
            return 'Quantità non valida';
        }
        // Include existing groups in total meters calculation
        const existingMeters = barGroups.reduce((sum, g) => sum + g.quantity * g.barLength, 0);
        const totalMeters = existingMeters + quantityNum * barLengthInMeters;
        if (minimumSaleQty && totalMeters < minimumSaleQty) {
            return `Quantità minima richiesta: ${minimumSaleQty} metri totali (attuale: ${totalMeters} metri)`;
        }
        return null;
    };

    const selectVariantCode = (baseCode, totalMeters) => {
        if (baseCode.includes('V1') || baseCode.includes('V2')) {
            if (!totalMeters || totalMeters <= 0) return baseCode;
            const targetVariant = totalMeters >= 120 ? 'V1' : (totalMeters >= 18 ? 'V2' : null);
            if (!targetVariant) return baseCode;
            return baseCode.replace(/V[12]/g, targetVariant);
        }
        return baseCode;
    };

    // Calculate total meters including existing groups + current input
    const getTotalMeters = () => {
        const existing = barGroups.reduce((sum, g) => sum + g.quantity * g.barLength, 0);
        const current = selectedLength && quantity ? parseInt(quantity, 10) * selectedLength : 0;
        return existing + (isNaN(current) ? 0 : current);
    };

    useEffect(() => {
        const productCode = getCurrentProductCode();
        if (productCode && quantity && selectedLength) {
            const totalMeters = getTotalMeters();
            const finalCode = selectVariantCode(productCode, totalMeters);
            fetchArticleData(finalCode).then(data => {
                setArticleData(data);
                if (data) {
                    const validationMsg = validateQuantity(quantity, data.MinimumSaleQty, selectedLength);
                    setValidationError(validationMsg);
                }
            });
        }
    }, [quantity, selectedLength, state.orderDetails?.productCode, barGroups.length]);

    const handleAddGroup = () => {
        if (!selectedLength || !quantity || validationError) return;
        const qty = parseInt(quantity, 10);
        if (isNaN(qty) || qty <= 0) return;

        // Check if same length already exists, merge
        const existing = barGroups.find(g => g.barLength === selectedLength);
        if (existing) {
            setBarGroups(prev => prev.map(g =>
                g.barLength === selectedLength ? { ...g, quantity: g.quantity + qty } : g
            ));
        } else {
            setBarGroups(prev => [...prev, { barLength: selectedLength, quantity: qty }]);
        }
        setSelectedLength(null);
        setQuantity('');
        setValidationError(null);
        setArticleData(null);
    };

    const handleRemoveGroup = (index) => {
        setBarGroups(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = () => {
        // If there's a pending selection, add it first
        let finalGroups = [...barGroups];
        if (selectedLength && quantity && !validationError) {
            const qty = parseInt(quantity, 10);
            if (!isNaN(qty) && qty > 0) {
                const existing = finalGroups.find(g => g.barLength === selectedLength);
                if (existing) {
                    finalGroups = finalGroups.map(g =>
                        g.barLength === selectedLength ? { ...g, quantity: g.quantity + qty } : g
                    );
                } else {
                    finalGroups.push({ barLength: selectedLength, quantity: qty });
                }
            }
        }

        if (finalGroups.length === 0) return;

        // For backwards compatibility: if single group, pass barLength + quantity
        // If multiple groups, pass barGroups array
        if (finalGroups.length === 1) {
            onSelect({
                barLength: finalGroups[0].barLength,
                quantity: finalGroups[0].quantity,
                barGroups: finalGroups
            });
        } else {
            const totalMeters = finalGroups.reduce((sum, g) => sum + g.quantity * g.barLength, 0);
            onSelect({
                barLength: finalGroups[0].barLength, // primary for drainage
                quantity: Math.round(totalMeters / finalGroups[0].barLength), // approximate for compat
                barGroups: finalGroups
            });
        }
    };

    const totalGroupMeters = barGroups.reduce((sum, g) => sum + g.quantity * g.barLength, 0);
    const totalGroupBars = barGroups.reduce((sum, g) => sum + g.quantity, 0);
    const canSubmit = (barGroups.length > 0 || (selectedLength && quantity && !validationError)) && !loading;

    return (
        <div className="space-y-6">
            <h2 className="text-xl font-semibold text-center mb-6">FORNITURA A BARRE</h2>

            {/* Existing bar groups */}
            {barGroups.length > 0 && (
                <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-700">Barre configurate:</p>
                    {barGroups.map((group, i) => (
                        <div key={i} className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                            <div>
                                <span className="font-semibold text-green-800">{group.quantity} {group.quantity === 1 ? 'barra' : 'barre'}</span>
                                <span className="text-green-700"> da {group.barLength} metri</span>
                                <span className="text-green-600 text-sm ml-2">({group.quantity * group.barLength} mt)</span>
                            </div>
                            <button
                                onClick={() => handleRemoveGroup(i)}
                                className="text-red-400 hover:text-red-600 text-lg font-bold px-2"
                                title="Rimuovi"
                            >
                                ✕
                            </button>
                        </div>
                    ))}
                    <div className="text-sm text-gray-600 font-medium text-right">
                        Totale: {totalGroupBars} barre — {totalGroupMeters} metri
                    </div>
                </div>
            )}

            {/* Bar Length Selection */}
            <div className="space-y-4">
                <p className="text-sm font-medium text-gray-700 text-center">
                    {barGroups.length > 0 ? 'Aggiungi altre barre (opzionale):' : 'Seleziona lunghezza barra:'}
                </p>
                <div className="flex justify-center space-x-4">
                    <button
                        onClick={() => setSelectedLength(3)}
                        className={`p-4 border-2 rounded-lg flex-1 text-center
              ${selectedLength === 3 ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}
                    >
                        <div className="mb-2">
                            <span className="text-sm">3000</span>
                        </div>
                        <div className="text-sm font-medium">
                            BARRE A 3 METRI
                        </div>
                    </button>

                    {!isOnly3m && (
                        <button
                            onClick={() => setSelectedLength(6)}
                            className={`p-4 border-2 rounded-lg flex-1 text-center
                  ${selectedLength === 6 ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}
                        >
                            <div className="mb-2">
                                <span className="text-sm">6000</span>
                            </div>
                            <div className="text-sm font-medium">
                                BARRE A 6 METRI
                            </div>
                        </button>
                    )}
                </div>
                {isOnly3m && (
                    <p className="text-sm text-center text-gray-500">
                        Per questo profilo la fornitura a barre è disponibile solo a 3 metri.
                    </p>
                )}
            </div>

            {/* Quantity Input */}
            {selectedLength && (
                <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                        QUANTITA' BARRE DA {selectedLength} METRI
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="number"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={quantity}
                            onChange={(e) => setQuantity(e.target.value)}
                            min="1"
                            className={`flex-1 p-3 text-base border-2 rounded-lg focus:ring-1 focus:ring-red-500 outline-none ${validationError ? 'border-red-500' : 'border-gray-200 focus:border-red-500'}`}
                            placeholder="Inserisci la quantità"
                        />
                        {!isOnly3m && (
                            <button
                                onClick={handleAddGroup}
                                disabled={!quantity || validationError || loading}
                                className="px-4 py-3 bg-green-500 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-green-600 transition-colors text-sm whitespace-nowrap"
                            >
                                + Aggiungi
                            </button>
                        )}
                    </div>

                    {loading && (
                        <div className="flex items-center text-sm text-gray-500">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500 mr-2"></div>
                            Controllo minimi in corso...
                        </div>
                    )}

                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                            {error}
                        </div>
                    )}

                    {validationError && (
                        <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                            {validationError}
                        </div>
                    )}

                    {articleData && !validationError && selectedLength && quantity && (
                        <div className="text-sm text-green-600 bg-green-50 p-2 rounded">
                            {articleData.MinimumSaleQty > 0 ? (
                                <>✓ Quantità valida (totale: {getTotalMeters()} metri, minimo: {articleData.MinimumSaleQty} metri)</>
                            ) : (
                                <>✓ Quantità valida (totale: {getTotalMeters()} metri, nessun minimo)</>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Submit Button */}
            <div className="pt-4">
                <button
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    className="w-full py-2 px-4 bg-red-500 text-white rounded-lg
                   disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {loading ? 'Controllo in corso...' : 'Avanti'}
                </button>
            </div>
        </div>
    );
};

export default BarConfigSelector;
